import { useState } from 'react';
import {
  mapPurchasePackage,
  type PurchasePackageViewModel,
} from '../PackagesSection.helpers';
import type { CurrentUserPackage, Package } from '../api';

export type PackageActivationSelection = {
  activePackageExpiresAt?: string;
  item: PurchasePackageViewModel;
};

type PackageActivationRequest = {
  packageName: string;
  periodDays: number;
};

export function usePackageActivation(
  availablePackages: Package[],
  activePackages: CurrentUserPackage[]
) {
  const [request, setRequest] = useState<PackageActivationRequest | null>(null);

  function canRenew(activePackage: CurrentUserPackage) {
    return (
      getActivationSelection(
        { packageName: activePackage.name, periodDays: activePackage.period },
        availablePackages,
        activePackages
      ) !== null
    );
  }

  function openForPurchase(item: PurchasePackageViewModel) {
    setRequest({ packageName: item.name, periodDays: item.periodDays });
  }

  function openForRenewal(activePackage: CurrentUserPackage) {
    setRequest({
      packageName: activePackage.name,
      periodDays: activePackage.period,
    });
  }

  function close() {
    setRequest(null);
  }

  const selection = getActivationSelection(
    request,
    availablePackages,
    activePackages
  );

  return {
    canRenew,
    close,
    openForPurchase,
    openForRenewal,
    selection,
  };
}

function getActivationSelection(
  request: PackageActivationRequest | null,
  availablePackages: Package[],
  activePackages: CurrentUserPackage[]
): PackageActivationSelection | null {
  if (!request) return null;

  const activePackageExpiresAt = activePackages[0]?.expiresAt;

  const packageOffer = availablePackages.find(
    availablePackage =>
      availablePackage.name.trim().toLowerCase() ===
      request.packageName.trim().toLowerCase()
  );
  if (!packageOffer) return null;

  const hasRequestedPeriod = packageOffer.periods.some(
    period => period.value === request.periodDays
  );
  if (!hasRequestedPeriod) return null;

  const item = mapPurchasePackage(packageOffer, request.periodDays);

  return {
    activePackageExpiresAt,
    item,
  };
}
